import { NotificationBellComponent } from '../notification-bell/notification-bell';
import { ChangeDetectionStrategy, Component, HostListener, Input, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { AuthService, DEFAULT_NAV, EnumRouteName, LANG_SIGNAL, LsLogoComponent, LsSearchBoxComponent, NavLinkModel } from '@lyricstream/shared';
import { TranslateModule } from '@ngx-translate/core';
import { AccountMenuNavItem, AvatarMenu } from '../avatar-menu/avatar-menu';

/** Défilement (px) à partir duquel l'en-tête de l'accueil devient opaque. */
const SCROLLED_THRESHOLD_PX = 24;

/** Défilement (px) à partir duquel la recherche du héros est sortie de l'écran et celle de l'en-tête apparaît. */
const SEARCH_STUCK_THRESHOLD_PX = 320;

@Component({
  selector: 'ls-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, LsLogoComponent, LsSearchBoxComponent, AvatarMenu, NotificationBellComponent, TranslateModule],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  host: {
    '[class.home]': 'isHome()',
    // Sur l'accueil, l'en-tête flotte transparent au-dessus de l'image du héros tant qu'on n'a pas défilé
    '[class.over-hero]': 'isHome() && !scrolled()',
  },
})
export class AppHeaderComponent {
  @Input() navLinks: NavLinkModel[] = DEFAULT_NAV;
  /** Pages où le champ de recherche doit être masqué (segments de route, ex: 'search'). */
  @Input() hideSearchOn: string[] = [];
  routeName = EnumRouteName;

  /** Clé de traduction des liens de navigation par défaut (`path` ou `path/pathId`). */
  private static readonly NAV_KEYS: Record<string, string> = {
    [EnumRouteName.Discover]: 'HEADER.NAV.DISCOVER',
    [`${EnumRouteName.Admin}/${EnumRouteName.MyLibrary}`]: 'HEADER.NAV.LIBRARY',
    [EnumRouteName.Recueils]: 'HEADER.NAV.RECUEILS',
    [EnumRouteName.Genres]: 'HEADER.NAV.GENRES',
    [`${EnumRouteName.Admin}/${EnumRouteName.MyLyricForm}`]: 'HEADER.NAV.CONTRIBUTE',
    [EnumRouteName.Community]: 'HEADER.NAV.COMMUNITY',
  };

  /** Clé de traduction d'un lien ; `null` pour un lien personnalisé (on affiche alors son `label`). */
  protected navKey(link: NavLinkModel): string | null {
    return AppHeaderComponent.NAV_KEYS[link.pathId ? `${link.path}/${link.pathId}` : link.path] ?? null;
  }

  protected readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly langSignal = inject(LANG_SIGNAL, { optional: true });

  /** Accueil = `/` ou `/<lang>`, quels que soient la query string ou le fragment (`/fr?utm_source=…`). */
  private static isHomeUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return path === '/' || /^\/[a-z]{2}\/?$/.test(path);
  }

  protected readonly isHome = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => AppHeaderComponent.isHomeUrl((e as NavigationEnd).urlAfterRedirects)),
      startWith(AppHeaderComponent.isHomeUrl(this.router.url)),
    ),
    { initialValue: AppHeaderComponent.isHomeUrl(this.router.url) },
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected get showSearch(): boolean {
    // Sur l'accueil, la recherche est dans le héros ; elle remonte dans l'en-tête dès qu'on a défilé
    if (this.isHome() && !this.searchStuck()) return false;
    const pathname = (this.currentUrl() ?? '').split('?')[0];
    return !this.hideSearchOn.some(seg => pathname.split('/').includes(seg));
  }

  /** Panneau de recherche plein écran (mobile). */
  protected readonly mobileSearchOpen = signal(false);

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.mobileSearchOpen.set(false);
  }


  /** Vrai dès que la page a défilé (l'en-tête redevient opaque). */
  protected readonly scrolled = signal(false);

  /** Liens proposés dans le menu du compte (l'en-tête les masque sous 1024 px). */
  protected readonly navItems = computed<AccountMenuNavItem[]>(() =>
    this.navLinks.map((link) => ({
      key: this.navKey(link),
      label: link.label,
      commands: link.pathId ? [link.path, link.pathId] : [link.path],
    })),
  );

  /** Vrai quand la barre de recherche du héros est sortie de l'écran : la recherche passe dans l'en-tête. */
  protected readonly searchStuck = signal(false);

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    this.scrolled.set(window.scrollY > SCROLLED_THRESHOLD_PX);
    this.searchStuck.set(window.scrollY > SEARCH_STUCK_THRESHOLD_PX);
  }

  getLangValue(): string {
    return this.langSignal?.() ?? this.router.url.split('/').filter(Boolean)[0] ?? 'fr';
  }

  login(): void {
    // Popup plutôt que redirection pleine page : évite un rechargement complet (donc aucune
    // coupure de la lecture en cours) — repli automatique sur authService.login() si le popup
    // est bloqué par le navigateur (déjà géré dans loginInPopup()).
    this.authService.loginInPopup();
  }
}
